import { useEffect, useState } from "react";
import { useTheme } from "../context/ThemeContext";

export default function DepositSuccessModal({ isOpen, onClose, amount }) {
  const { isDarkMode } = useTheme();
  const [countdown, setCountdown] = useState(10);
  const [confettiPieces, setConfettiPieces] = useState([]);

  useEffect(() => {
    if (isOpen) {
      // Generate confetti pieces
      const pieces = Array.from({ length: 50 }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 2,
        duration: 2 + Math.random() * 2,
        color: ['#FFD700', '#FF6B6B', '#4CAF50', '#2196F3', '#9C27B0'][Math.floor(Math.random() * 5)]
      }));
      setConfettiPieces(pieces);

      // Start countdown
      setCountdown(10);
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            setTimeout(onClose, 100);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const dark = isDarkMode;

  const styles = {
    overlay: {
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.7)",
      backdropFilter: "blur(8px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 2000,
      animation: "fadeIn 0.3s ease-out",
    },
    modal: {
      position: "relative",
      width: "100%",
      maxWidth: 420,
      background: dark
        ? "linear-gradient(135deg, #1a1a1a 0%, #2d1b1b 100%)"
        : "linear-gradient(135deg, #ffffff 0%, #f8f8f8 100%)",
      borderRadius: 24,
      padding: "3rem 2rem",
      border: `2px solid ${dark ? "#4CAF50" : "#4CAF50"}`,
      boxShadow: "0 25px 70px rgba(0,0,0,0.4), 0 0 40px rgba(76, 175, 80, 0.3)",
      textAlign: "center",
      animation: "scaleIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
      overflow: "hidden",
    },
    confettiContainer: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      pointerEvents: "none",
      overflow: "hidden",
    },
    confettiPiece: {
      position: "absolute",
      width: 10,
      height: 10,
      borderRadius: "50%",
      top: -20,
    },
    successIcon: {
      width: 80,
      height: 80,
      margin: "0 auto 1.5rem",
      background: "linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 40,
      boxShadow: "0 8px 20px rgba(76, 175, 80, 0.3)",
      animation: "bounceIn 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55)",
      position: "relative",
      zIndex: 1,
    },
    title: {
      fontSize: 24,
      fontWeight: 700,
      color: dark ? "#f0f0f0" : "#111",
      margin: "0 0 0.5rem",
      letterSpacing: "-0.02em",
      position: "relative",
      zIndex: 1,
    },
    subtitle: {
      fontSize: 16,
      color: dark ? "#aaa" : "#666",
      margin: "0 0 1.5rem",
      lineHeight: 1.5,
      position: "relative",
      zIndex: 1,
    },
    amount: {
      fontSize: 36,
      fontWeight: 700,
      color: "#4CAF50",
      margin: "1rem 0",
      letterSpacing: "-0.03em",
      animation: "pulse 2s infinite",
      position: "relative",
      zIndex: 1,
    },
    divider: {
      width: 60,
      height: 3,
      background: "linear-gradient(90deg, transparent, #4CAF50, transparent)",
      margin: "1.5rem auto",
      borderRadius: 2,
      position: "relative",
      zIndex: 1,
    },
    message: {
      fontSize: 14,
      color: dark ? "#ccc" : "#555",
      margin: "1rem 0",
      padding: "1rem",
      background: dark ? "rgba(76, 175, 80, 0.1)" : "rgba(76, 175, 80, 0.08)",
      borderRadius: 12,
      border: `1px solid ${dark ? "rgba(76, 175, 80, 0.3)" : "rgba(76, 175, 80, 0.2)"}`,
      position: "relative",
      zIndex: 1,
    },
    countdown: {
      fontSize: 13,
      color: dark ? "#888" : "#666",
      margin: "1.5rem 0 0",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      position: "relative",
      zIndex: 1,
    },
    countdownNumber: {
      display: "inline-block",
      padding: "4px 8px",
      background: dark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)",
      borderRadius: 6,
      fontWeight: 600,
      color: dark ? "#f0f0f0" : "#333",
      minWidth: 24,
    },
    closeBtn: {
      padding: "12px 24px",
      background: "linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)",
      border: "none",
      borderRadius: 10,
      color: "#fff",
      fontSize: 14,
      fontWeight: 600,
      cursor: "pointer",
      marginTop: "1rem",
      boxShadow: "0 4px 12px rgba(76, 175, 80, 0.3)",
      transition: "all 0.2s",
      position: "relative",
      zIndex: 1,
    },
    sparkle: {
      position: "absolute",
      color: "#FFD700",
      fontSize: 20,
      animation: "sparkle 1.5s ease-in-out infinite",
    },
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        {/* Confetti Animation */}
        <div style={styles.confettiContainer}>
          {confettiPieces.map(piece => (
            <div
              key={piece.id}
              style={{
                ...styles.confettiPiece,
                left: `${piece.left}%`,
                background: piece.color,
                animationDelay: `${piece.delay}s`,
                animationDuration: `${piece.duration}s`,
                animation: `confettiFall ${piece.duration}s linear ${piece.delay}s`,
              }}
            />
          ))}
        </div>

        {/* Sparkles around success icon */}
        <div style={{ position: "relative" }}>
          <span style={{ ...styles.sparkle, top: -10, left: -10, animationDelay: "0s" }}>✨</span>
          <span style={{ ...styles.sparkle, top: -10, right: -10, animationDelay: "0.5s" }}>✨</span>
          <span style={{ ...styles.sparkle, bottom: -10, left: -10, animationDelay: "1s" }}>✨</span>
          <span style={{ ...styles.sparkle, bottom: -10, right: -10, animationDelay: "1.5s" }}>✨</span>
        </div>

        <div style={styles.successIcon}>
          <span>✓</span>
        </div>

        <h2 style={styles.title}>Deposit Successful!</h2>
        <p style={styles.subtitle}>Your payment has been received</p>

        {amount && (
          <div style={styles.amount}>₹{amount.toLocaleString()}</div>
        )}

        <div style={styles.divider} />

        <div style={styles.message}>
          <strong>🎉 Congratulations!</strong>
          <br />
          Your funds will be credited to your wallet within 2-4 hours after admin verification.
          You will receive a notification once the amount is credited.
        </div>

        <div style={styles.countdown}>
          <span>Closing in</span>
          <span style={styles.countdownNumber}>{countdown}</span>
          <span>seconds</span>
        </div>

        <button
          style={styles.closeBtn}
          onClick={onClose}
          onMouseEnter={(e) => {
            e.target.style.transform = "scale(1.05)";
            e.target.style.boxShadow = "0 6px 16px rgba(76, 175, 80, 0.4)";
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = "scale(1)";
            e.target.style.boxShadow = "0 4px 12px rgba(76, 175, 80, 0.3)";
          }}
        >
          Close Now
        </button>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.8);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes bounceIn {
          0% {
            opacity: 0;
            transform: scale(0.3);
          }
          50% {
            transform: scale(1.1);
          }
          70% {
            transform: scale(0.9);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.05);
          }
        }

        @keyframes sparkle {
          0%, 100% {
            opacity: 0;
            transform: scale(0) rotate(0deg);
          }
          50% {
            opacity: 1;
            transform: scale(1) rotate(180deg);
          }
        }

        @keyframes confettiFall {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
}